import { useState } from "react";
import { PERIOD_PILLS, type PeriodKey } from "@easyshop/shared";
import { cx } from "@/components/ui";
import { CalendarIcon, MonthCalendar } from "@/components/PeriodBar";
import { ymd } from "@/components/planning/utils";
import { usePeriod } from "@/hooks/usePeriod";
import { SHORTCUTS, canShiftNext, cmpChoices, cmpRange, isShortcut, periodLabel, rangeLabel, shiftSel, type PeriodSel } from "@/lib/period-nav";
import { IosGroup, IosSection, IosSegmented } from "./MobileUi";

const PERIOD_OPTIONS = (Object.keys(PERIOD_PILLS) as PeriodKey[]).map((key) => ({ key, label: PERIOD_PILLS[key] }));
/** Pas de plage libre sur mobile : raccourcis sans « 30 derniers jours » ni « Cette semaine / Ce mois-ci ». */
const MOBILE_SHORTCUTS = SHORTCUTS.filter((s) => ["Aujourd'hui", "Hier", "Semaine dernière", "Mois dernier", "Cette année"].includes(s.label));

/**
 * Feuille du bas « Choisir une date » : période, raccourcis, calendrier et
 * comparaison. Un jour, un raccourci ou une comparaison s'applique et ferme ;
 * « Annuler » abandonne le changement de période.
 */
function DateSheet({ onClose }: { onClose: () => void }) {
  const s = usePeriod();
  const [draft, setDraft] = useState<PeriodSel>(() => ({
    period: s.period === "perso" ? "jour" : s.period,
    date: s.period === "perso" ? s.range?.to ?? null : s.date,
    range: null,
    cmp: s.cmp,
  }));
  const commit = (patch: Partial<PeriodSel>) => {
    s.apply({ ...draft, ...patch });
    onClose();
  };
  return (
    <div role="dialog" className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-[rgba(0,0,0,0.3)]" onClick={onClose} />
      <div className="ios relative mx-auto max-h-[92dvh] w-full max-w-[480px] overflow-y-auto rounded-t-[14px] bg-ios-bg pb-[calc(16px+env(safe-area-inset-bottom))]">
        <div className="sticky top-0 z-[1] flex h-12 items-center justify-between border-b-[0.5px] border-ios-sep bg-ios-bar px-4 backdrop-blur-xl">
          <button type="button" onClick={onClose} className="text-[17px] text-accent">
            Annuler
          </button>
          <span className="text-[17px] font-semibold">Choisir une date</span>
          <button type="button" onClick={() => commit({ period: "jour", date: null })} className="text-[17px] font-semibold text-accent">
            Aujourd'hui
          </button>
        </div>
        <div className="flex flex-col gap-[18px] px-4 pt-4">
          <IosSegmented options={PERIOD_OPTIONS} value={draft.period as PeriodKey} onChange={(period) => setDraft((d) => ({ ...d, period }))} />
          <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4">
            {MOBILE_SHORTCUTS.map((sc) => (
              <button
                key={sc.label}
                type="button"
                onClick={() => commit(sc.sel())}
                className={cx(
                  "min-h-[32px] flex-shrink-0 whitespace-nowrap rounded-full px-[14px] text-[15px] font-medium",
                  isShortcut(s, sc) ? "bg-accent text-white" : "bg-ios-fill text-ink"
                )}
              >
                {sc.label}
              </button>
            ))}
          </div>
          <div className="rounded-ios bg-white px-2 pb-2 pt-3">
            <MonthCalendar sel={draft} cell={42} onPick={(day) => commit({ date: ymd(day) })} />
          </div>
          <IosSection title="Les écarts ▲ ▼ sont calculés par rapport à">
            <IosGroup>
              {cmpChoices(draft).map((c) => (
                <button key={c.key} type="button" onClick={() => commit({ cmp: c.key })} className="flex min-h-[52px] w-full items-center gap-3 bg-white pl-4 text-left">
                  <span className="ios-sep flex min-w-0 flex-1 items-center gap-2 self-stretch py-2 pr-4">
                    <span className="min-w-0 flex-1">
                      <span className="block text-[16px]">{c.title}</span>
                      <span className="block text-[13px] text-muted">{rangeLabel(draft.period, cmpRange(draft, c.key))}</span>
                    </span>
                    {draft.cmp === c.key && <span className="text-[18px] text-accent">✓</span>}
                  </span>
                </button>
              ))}
            </IosGroup>
          </IosSection>
        </div>
      </div>
    </div>
  );
}

/**
 * ‹ période › du mobile : chevrons accent, libellé (calendrier ▾) qui ouvre la
 * feuille de date. `round` : chevrons dans des pastilles blanches (accueil).
 */
export function MobileDateNav({ round = false }: { round?: boolean }) {
  const s = usePeriod();
  const [open, setOpen] = useState(false);
  const arrow = round
    ? "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white text-[20px] leading-none text-accent disabled:opacity-35"
    : "px-2 py-[6px] text-[20px] leading-none text-accent disabled:opacity-30";
  return (
    <div className={cx("flex items-center justify-between", round ? "gap-2 px-[2px]" : "px-1")}>
      <button type="button" aria-label="Période précédente" className={arrow} onClick={() => s.apply(shiftSel(s, -1))}>
        ‹
      </button>
      <button type="button" onClick={() => setOpen(true)} className={cx("flex min-h-[36px] min-w-0 items-center text-[15px] font-semibold", round ? "gap-[7px]" : "gap-[6px]")}>
        <span className="flex-shrink-0 text-accent">
          <CalendarIcon />
        </span>
        <span className="truncate">{periodLabel(s)}</span>
        <span aria-hidden className="text-[11px] text-muted">
          ▾
        </span>
      </button>
      <button type="button" aria-label="Période suivante" disabled={!canShiftNext(s)} className={arrow} onClick={() => s.apply(shiftSel(s, 1))}>
        ›
      </button>
      {open && <DateSheet onClose={() => setOpen(false)} />}
    </div>
  );
}
